import aa from "search-insights";

/**
 * Algolia Insights(클릭/전환 이벤트) 클라이언트 (JCG-2181).
 *
 * 검색→클릭→전환 흐름을 queryID로 묶어 Algolia 대시보드 Insights에 전송한다.
 * 이후 A/B 테스트·Recommend·재랭킹(Dynamic Re-Ranking)의 데이터 연료가 된다.
 *
 * ## userToken 정책 (익명/로그인)
 * Algolia 권장 방식을 따른다.
 * - `userToken`: **항상** 브라우저별 익명 토큰(localStorage 보존). 로그인 여부와 무관하게 유지돼
 *   로그인 이전/이후 이벤트가 한 사용자로 이어진다.
 * - `authenticatedUserToken`: 로그인 사용자만 추가로 Clerk userId 전달. 기기 간 동일 사용자 매칭.
 *
 * 쿠키는 사용하지 않는다(`useCookie: false`) — 익명 토큰을 직접 관리해 동의 배너 의존을 피한다.
 */

/** 익명 사용자 토큰 보존 키 (localStorage) */
const ANON_TOKEN_KEY = "algolia_anon_user_token";
/** 검색 클릭→전환 연결용 임시 보존 키 (sessionStorage) */
const PENDING_CONVERSION_KEY = "algolia_pending_conversion";
/** 전환을 검색 직후 흐름으로만 인정하는 유효 시간 (밀리초) */
const PENDING_CONVERSION_TTL_MS = 5 * 60 * 1000;

let credentials: { appId: string; apiKey: string } | null = null;
let initializedCredentialKey: string | null = null;

export function configureAlgoliaInsightsCredentials(nextCredentials: {
  appId: string;
  apiKey: string;
}): void {
  credentials = nextCredentials;
}

/** appId/apiKey가 있고 브라우저 환경일 때만 1회 init. 성공 여부 반환. */
function ensureInit(): boolean {
  if (typeof window === "undefined" || !credentials) return false;
  const credentialKey = `${credentials.appId}:${credentials.apiKey}`;
  if (initializedCredentialKey !== credentialKey) {
    aa("init", {
      appId: credentials.appId,
      apiKey: credentials.apiKey,
      useCookie: false,
    });
    initializedCredentialKey = credentialKey;
  }
  return true;
}

/** 브라우저별 익명 토큰: 없으면 생성해 localStorage에 보존. */
function getAnonymousUserToken(): string {
  if (typeof window === "undefined") return "anonymous";
  try {
    let token = window.localStorage.getItem(ANON_TOKEN_KEY);
    if (!token) {
      token = `anon-${crypto.randomUUID()}`;
      window.localStorage.setItem(ANON_TOKEN_KEY, token);
    }
    return token;
  } catch {
    // localStorage 접근 불가(프라이빗 모드 등) → 세션 한정 임시 토큰
    return `anon-${crypto.randomUUID()}`;
  }
}

/**
 * 로그인 userId(Clerk)를 받아 이벤트 공통 토큰을 만든다.
 * userToken은 항상 익명 토큰, 로그인 시에만 authenticatedUserToken을 채운다.
 */
function resolveTokens(userId?: string | null): {
  userToken: string;
  authenticatedUserToken?: string;
} {
  const userToken = getAnonymousUserToken();
  return userId ? { userToken, authenticatedUserToken: userId } : { userToken };
}

type SearchContext = {
  /** Algolia 인덱스명 */
  index: string;
  /** clickAnalytics로 받은 검색 queryID */
  queryID: string;
  /** 클릭한 결과의 objectID */
  objectID: string;
};

/**
 * Insights 이벤트를 fire-and-forget으로 전송한다(텔레메트리는 절대 호출부를 깨지 않는다).
 * - 동기 throw는 try/catch로 흡수.
 * - aa(...)는 sendBeacon/XHR 기반 Promise를 반환 → 네트워크 실패 시 reject 되므로
 *   `.catch()`로 잡아 unhandled promise rejection(Sentry 노이즈)을 막는다.
 */
function safeSend(
  method: "clickedObjectIDsAfterSearch" | "convertedObjectIDsAfterSearch",
  payload: Record<string, unknown>
): void {
  try {
    // aa(...)는 Promise|undefined 반환 → resolve로 정규화해 reject(네트워크 실패)를 흡수
    void Promise.resolve(aa(method, payload)).catch(() => {});
  } catch {
    // 텔레메트리 실패는 무시 (검색/네비게이션 흐름에 영향 없음)
  }
}

/**
 * 검색 결과 클릭 이벤트 전송 (`clickedObjectIDsAfterSearch`).
 * position은 1-based(첫 결과 = 1).
 */
export function sendSearchResultClicked(
  ctx: SearchContext & { position: number },
  userId?: string | null
): void {
  if (!ensureInit()) return;
  safeSend("clickedObjectIDsAfterSearch", {
    eventName: "Search Result Clicked",
    index: ctx.index,
    queryID: ctx.queryID,
    objectIDs: [ctx.objectID],
    positions: [ctx.position],
    ...resolveTokens(userId),
  });
}

/**
 * 검색 결과 전환 이벤트 전송 (`convertedObjectIDsAfterSearch`).
 * 클릭 후 목적지 콘텐츠 페이지에 실제 도달·체류했을 때 호출한다.
 */
export function sendSearchResultConverted(
  ctx: SearchContext,
  tokens: { userToken: string; authenticatedUserToken?: string }
): void {
  if (!ensureInit()) return;
  safeSend("convertedObjectIDsAfterSearch", {
    eventName: "Search Result Viewed",
    index: ctx.index,
    queryID: ctx.queryID,
    objectIDs: [ctx.objectID],
    ...tokens,
  });
}

/** sessionStorage에 보존하는 전환 대기 데이터 */
export type PendingConversion = SearchContext & {
  userToken: string;
  authenticatedUserToken?: string;
  /** 전환을 인정할 목적지 경로(pathname). 이 경로에 도달했을 때만 전환 전송. */
  path: string;
  /** stash 시각(ms). TTL 초과분은 무시. */
  ts: number;
};

/**
 * 클릭 직후 전환 연결용 컨텍스트를 sessionStorage에 보존한다.
 * 목적지 경로(path)에 도달하면 트래커가 읽어 전환 이벤트를 전송한다.
 */
export function stashSearchClickForConversion(
  ctx: SearchContext,
  path: string,
  userId?: string | null
): void {
  if (typeof window === "undefined") return;
  try {
    const payload: PendingConversion = {
      ...ctx,
      ...resolveTokens(userId),
      path,
      ts: Date.now(),
    };
    window.sessionStorage.setItem(
      PENDING_CONVERSION_KEY,
      JSON.stringify(payload)
    );
  } catch {
    // sessionStorage 접근 불가 시 전환 연결만 생략 (클릭 이벤트는 이미 전송됨)
  }
}

/**
 * 보존된 전환 대기 데이터를 삭제하지 않고 읽기만 한다(peek).
 * TTL 초과·파싱 실패 시 null. 실제 소비(전송) 시점에 별도로 {@link clearPendingConversion} 호출.
 *
 * remove를 전송 직전까지 미루는 이유: React strict mode(개발)의 effect 이중 호출에서
 * 마운트마다 destructive read를 하면 전환이 한 번도 전송되지 않을 수 있다. peek로 분리해
 * 두 번째 마운트에서도 동일 데이터를 읽고, dwell 만료 시 1회만 clear+전송한다.
 */
export function peekPendingConversion(): PendingConversion | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(PENDING_CONVERSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingConversion;
    if (!parsed.queryID || !parsed.objectID || !parsed.path) return null;
    if (Date.now() - parsed.ts > PENDING_CONVERSION_TTL_MS) {
      window.sessionStorage.removeItem(PENDING_CONVERSION_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/** 보존된 전환 대기 데이터를 삭제한다(전송 후 1회 사용 보장). */
export function clearPendingConversion(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(PENDING_CONVERSION_KEY);
  } catch {
    // 무시
  }
}
